import {
  DrawerDescription,
  FormSwitchGroup,
  PopoverTrigger,
  PopoverContent,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormSelect,
  FormInput,
  Popover,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { Cancel, Delete, Info } from '@ui/components/icons';
import { PhoneCodes, Masks } from '@helpers/constant';
import { HasPermission } from '@shared/components';
import { UserTableDto } from '@interfaces/models';
import { useHasPermission } from '@shared/hooks';
import { FC } from 'react';

import { useUserEdit } from './use-user-edit';

interface UserEditSidebarProps {
  open?: boolean;
  user: UserTableDto;
  onRemoveClick?: () => void;
  onClose?: ({ handleSuccess }: { handleSuccess: boolean }) => void;
}

export const UserEditSidebar: FC<UserEditSidebarProps> = ({
  user,
  onRemoveClick = () => {},
  onClose = () => {},
}) => {
  const hasEditPermission = useHasPermission('COMPANY_ACCOUNT_EDIT_USER', 'WRITE');
  const {
    isEditUserPending,
    roleOptions,
    form,
    t,
    onSubmit,
    isCostCentersFetching,
    costCentersOptions,
  } = useUserEdit(user, () => onClose({ handleSuccess: true }));

  const email = form.watch('email');

  return (
    <Drawer
      onClose={() => {
        onClose({ handleSuccess: false });
        form.reset();
      }}
      open={!!open}
      handleOnly
    >
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle asChild>
            <Typography variant="h200">{t('USERS.EDIT_USER_SIDEBAR_HEADER')}</Typography>
          </DrawerTitle>
          <DrawerDescription />
          <div>
            <HasPermission permissionFeature="COMPANY_ACCOUNT_EDIT_USER" permissionType="WRITE">
              <Button onClick={onRemoveClick} variant="ghost" icon>
                <Delete />
              </Button>
            </HasPermission>
            <DrawerClose asChild>
              <Button variant="ghost" size="small" icon>
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <form
          className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex-1">
            <div className="space-y-5 border-b border-border-weak pb-4">
              <Typography variant="labelMediumBold">
                {t('USERS.INVITE_USER_SIDEBAR_FORM_SECTION_LABEL')}
              </Typography>

              <Form {...form}>
                <div className="grid grid-cols-6 gap-2 items-center">
                  <Label className="col-span-2" htmlFor="firstName">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('USERS.FIRST_NAME')}
                    </Typography>
                  </Label>
                  <FormInput
                    componentProps={{
                      id: 'firstName',
                    }}
                    placeholder={t('USERS.FIRST_NAME')}
                    className="w-full col-span-4"
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    name="firstName"
                    size="small"
                    required
                  />

                  <Label className="col-span-2" htmlFor="lastName">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('USERS.LAST_NAME')}
                    </Typography>
                  </Label>
                  <FormInput
                    componentProps={{
                      id: 'lastName',
                    }}
                    placeholder={t('USERS.LAST_NAME')}
                    className="w-full col-span-4"
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    name="lastName"
                    size="small"
                    required
                  />

                  <Label className="col-span-2">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('USERS.EMAIL')}
                    </Typography>
                  </Label>
                  <div className="flex items-center gap-4 justify-between col-span-4">
                    <Typography variant="labelMediumBook" className="text-disabled" element="span">
                      {email}
                    </Typography>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" type="button" size="small" icon>
                          <Info />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="max-w-47" align="end">
                        <Typography className="text-paragraph" variant="labelMediumBook">
                          {t('USERS.EMAIL_CHANGE_TOOLTIP')}
                        </Typography>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Label className="col-span-2" htmlFor="phoneNumber">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('USERS.PHONE_NUMBER')}
                    </Typography>
                  </Label>
                  <div className="grid grid-cols-4 gap-2 col-span-4">
                    <FormSelect
                      disabled={!hasEditPermission}
                      formControl={form.control}
                      className="col-span-1"
                      options={PhoneCodes}
                      name="phoneExt"
                      size="small"
                    />
                    <FormInput
                      componentProps={{
                        maskOptions: { mask: Masks.PHONE },
                        id: 'phoneNumber',
                      }}
                      placeholder={t('USERS.PHONE_NUMBER')}
                      className="w-full col-span-3"
                      disabled={!hasEditPermission}
                      formControl={form.control}
                      name="phoneNumber"
                      size="small"
                    />
                  </div>

                  <Label className="col-span-2">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('USERS.ROLE')}
                    </Typography>
                  </Label>
                  <FormSelect
                    placeholder={t('USERS.ROLE')}
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    className="col-span-4"
                    options={roleOptions}
                    name="role"
                  />
                </div>
              </Form>
            </div>

            {costCentersOptions.length > 1 && (
              <div className="space-y-6 mt-5">
                <Typography variant="labelMediumBold">
                  {t('USERS.INVITE_USER_SIDEBAR_COST_CENTER_TITLE')}
                </Typography>
                <Form {...form}>
                  <FormSwitchGroup
                    switchClassName="col-span-1 justify-self-end"
                    className="grid grid-cols-6 gap-4"
                    options={costCentersOptions}
                    labelClassName="col-span-5"
                    control={form.control}
                    name="costCenters"
                  />
                </Form>
              </div>
            )}
          </div>
        </form>
        <DrawerFooter>
          <Button
            disabled={isEditUserPending || isCostCentersFetching || !hasEditPermission}
            isLoading={isEditUserPending || isCostCentersFetching}
            onClick={form.handleSubmit(onSubmit)}
            className="shrink-0"
            type="submit"
            size="large"
            wide
          >
            {t('USERS.EDIT_USER_SIDEBAR_SUBMIT')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
